import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LogOut, ChevronDown, Settings, User, Bell, 
  Menu, X, Shield, Search
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CollegeNavbar = ({ isSidebarOpen, setIsSidebarOpen, collegeInfo }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const menuItems = [
    { label: 'View Profile', icon: User, href: '/college/profile' },
    { label: 'Settings', icon: Settings, href: '/college/settings' },
    { label: 'Admin Panel', icon: Shield, href: '/college/admin' },
  ];

  useEffect(() => {
    fetchNotification();
  }, []);


  const fetchNotification = async () => {
    try{
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/notifications`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if(!response.ok) throw new Error("Failed to fetch notifications");
      const data = await response.json();
      setNotifications(data);
      console.log("notification ",data);
    }catch(err){
      console.error("Error fetching notifications:", err);
    }
  }

  const handleReadNotification = async (notificationId) => {
    try{
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/notifications/${notificationId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if(!response.ok) throw new Error("Failed to read notification");
      
    }catch(err){
      console.error("Error reading notification:", err);
    }finally{
      fetchNotification();
    }
  }

  return (
    <nav className="bg-white border-b border-gray-100 fixed w-full top-0 z-40">
      <div className="max-w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 lg:hidden"
            >
              {isSidebarOpen ? (
                <X className="h-5 w-5 text-gray-500" />
              ) : (
                <Menu className="h-5 w-5 text-gray-500" />
              )}
            </button>

            <div className="relative hidden sm:block w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-1.5 text-sm bg-gray-50 border-0
                  rounded-lg focus:ring-1 focus:ring-blue-500 focus:bg-white"
              />
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
           {/* Notifications */}
           <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsNotificationOpen((prev) => !prev)}
                className="relative p-2 rounded-xl bg-gradient-to-b from-gray-50 to-gray-100 
                  hover:from-gray-100 hover:to-gray-200 border border-gray-200/50 transition-all 
                  duration-200"
              >
                <div className="relative">
                  <Bell className="w-5 h-5 text-gray-600" />
                  {notifications?.length > 0 && (
                    <span
                      className="absolute -top-2 -right-1 w-4 h-4 bg-red-500 text-white text-xs 
                      rounded-full flex items-center justify-center"
                    >
                      {notifications?.length}
                    </span>
                  )}
                </div>
                <div
                  className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/0 
                  to-blue-400/0 hover:from-blue-400/5 hover:to-purple-400/5 transition-colors"
                />
                
              </motion.button>
              {isNotificationOpen && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="absolute right-10 mt-2 w-200 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5"
                    style={{ top: '100%' }}
                  >
                    <div className="p-4 text-left">
                      <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
                      {notifications?.length === 0 ? (
                        <div className="mt-4 text-sm text-gray-500 w-200">No new notifications.</div>
                      ) : (
                        <ul className="mt-4 space-y-4 max-h-80 overflow-y-auto">
                          {notifications?.map((notification, index) => (
                            <li key={index} className="flex items-start space-x-4 border-b border-gray-200 pb-2">
                              <div className="flex-shrink-0">
                                <Bell className="w-6 h-6 text-blue-500" />
                              </div>
                              <div className="flex-1 text-left">
                                <p className="text-sm font-medium text-gray-900">{notification?.title}</p>
                                <p className="text-sm text-gray-500">{notification?.message}</p>
                                <p className="text-xs text-gray-400">{new Date(notification?.createdAt).toLocaleString()}</p>
                              </div>
                              <button
                                onClick={() => handleReadNotification(notification?._id)}
                                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </motion.div>
                )}

            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100"
              >
                {collegeInfo?.logo ? (
                  <img
                    src={collegeInfo.logo}
                    alt="Profile"
                    className="h-8 w-8 rounded-lg object-cover ring-2 ring-white"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center">
                    <span className="text-white font-medium">
                      {collegeInfo?.name?.charAt(0) || 'C'}
                    </span>
                  </div>
                )}
                <ChevronDown 
                  className={`h-4 w-4 text-gray-500 transition-transform duration-200
                    ${isProfileOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {isProfileOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border py-2 z-50"
                  >
                    <div className="px-4 py-2 border-b">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {collegeInfo?.name || 'College Name'}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {collegeInfo?.email || 'admin@college.edu'}
                      </p>
                    </div>

                    <div className="py-1">
                      {menuItems.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <item.icon className="h-4 w-4 mr-3 text-gray-500" />
                          {item.label}
                        </Link>
                      ))}
                    </div>

                    <div className="border-t">
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Logout
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default CollegeNavbar;